<template>
  <Layout>
    <Loader v-if="!showSelector" />
    <CountryLanguageSelector v-else :countries="resources" />
  </Layout>
</template>

<script>
import detectNearestBrowserLocale from 'detect-nearest-browser-locale'
import { Resource, LanguageFolder, CountryFolder } from 'beisheim/models'
import Loader from '@i22/frontend-cms-utils/components/loader'
import CountryLanguageSelector from 'beisheim/components/elements/country-language-selector'
import withMessages from '@i22/frontend-cms-utils/mixins/with-messages'
import externalScripts from 'beisheim/mixins/external-scripts'
import Layout from '@/layouts/minimal'

const DEFAULT_COUNTRY = 'de';
const DEFAULT_LANGUAGE = 'de';

export default {
  components: {
    Loader,
    Layout,
    CountryLanguageSelector
  },
  mixins: [ withMessages, externalScripts ],
  asyncData () {
    const resources = Resource.query().where('resource_class_name', 'CountryFolder').get()
    const [ allowedCountries, allowedLanguages ] = [ CountryFolder, LanguageFolder ].map(klass => klass.all().map(c => c.name))
    return {
      resources,
      allowedCountries,
      allowedLanguages,
      showSelector: false,
    }
  },
  computed: {
    tld() {
      const hostname = window.location.host
      return hostname.split('.').pop().toLowerCase()
    },
    isRedirectAllowed() {
      return !process.env.DISABLE_LOCALE_REDIRECT
    }
  },
  async mounted () {
    // redirect to home page of prefered language only if it exists
    if (this.isRedirectAllowed) {
      if (this.$cms) {
        this.routeChangedMessage({
          to: `/${this.$route.params.country || DEFAULT_COUNTRY}/${DEFAULT_LANGUAGE}`,
          from: window.location.pathname
        })
        return;
      }

      const [preferredCountry, preferredLanguage] = await this.getPreferredLocale()
      const Language = await LanguageFolder.query().where(r =>
        r.columns.language === preferredLanguage &&
        r.columns.country === preferredCountry
      ).first()

      if (Language) {
        window.location.href = `/${preferredCountry}/${preferredLanguage}${window.location.search}`
      } else {
        this.showSelector = true;
      }
    }
  },
  methods: {
    async getPreferredLocale() {
      if (this.allowedCountries.includes(this.$route.params.country)) {
        return [this.$route.params.country, DEFAULT_LANGUAGE]
      }

      if (this.allowedCountries.includes(this.tld)) {
        return [this.tld, DEFAULT_LANGUAGE]
      }

      if (process.env.IPSTACK_API_KEY) {
        return this.getPreferredLocaleByIP()
      }

      return this.getPreferredLocaleByBrowser();
    },
    async getPreferredLocaleByIP() {
      const ipLocationData = await fetch(
        `https://api.ipstack.com/check?access_key=${process.env.IPSTACK_API_KEY}&fields=country_code,location.languages`
      ).then(response => response.json())
      const countryLanguages =
        ipLocationData.languages?.map((language) => language.code) ||
        ipLocationData.location?.languages?.map((language) => language.code)
      const preferredCountry = ipLocationData.country_code.toLowerCase();
      const preferredLanguage = this.allowedLanguages.find((language) => countryLanguages.includes(language)) || countryLanguages[0]
      return [preferredCountry, preferredLanguage]
    },
    getPreferredLocaleByBrowser() {
      const supportedLocales = []
      this.allowedLanguages.forEach((allowedLanguage) => {
        this.allowedCountries.forEach((allowedCountry) => {
          const newValue = allowedLanguage + '-' + allowedCountry.toUpperCase()
          if (!supportedLocales.includes(newValue)) {
            supportedLocales.push(newValue)
          }
        })
      })
      const language = detectNearestBrowserLocale(supportedLocales)
      const [preferredLanguage, preferredCountry] = language.split('-').map(e => e.toLowerCase())

      return [preferredCountry, preferredLanguage]
    }
  },
  head () {
    return this.getExternalScripts();
  }
}
</script>

<style lang="scss">
@import '~beisheim/scss/colors';
@import '~beisheim/scss/scale';

.country-language-redirector {
  display: flex;
  width: 100vw;
  height: 100vh;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  &__logo {
    width: 250px;
    margin: scale(4);
  }

  &__select {
    min-width: 500px;
    padding: scale(6);
    background-color: gray(4);
  }
}
</style>
